import { gql } from 'apollo-angular';

// Query to get companies for selection
export const GET_COMPANIES_QUERY = gql`
  query GetCompanies {
    Companies {
      results {
        id
        name
        sapBaseUrl
        sapCompanyDb
      }
      totalCount
    }
  }
`;

// Query to get existing field mappings for a company
export const GET_COMPANY_SAP_MAPPINGS_QUERY = gql`
  query GetCompanySapMappings($companyId: Int!) {
    sapFieldMappings(companyId: $companyId) {
      id
      ourField
      sapFieldPath
      arrayStrategy
      counterVisible
      extraFields
    }
  }
`;

// Query to get existing SAP query templates
export const GET_SAP_QUERY_TEMPLATES_QUERY = gql`
  query GetSapQueryTemplates($companyId: Int!) {
    odataTemplates(companyId: $companyId) {
      id
      name
      description
      category
      primaryEndpoint
      primaryQueryParams
      updatedAt
    }
  }
`;

// Mutation to fetch sample SAP data for field discovery
export const FETCH_SAP_SAMPLE_DATA_MUTATION = gql`
  mutation FetchSapSampleData($companyId: ID!) {
    fetchSapSampleData(companyId: $companyId) {
      success
      message
      error
      itemCode
      discoveredFields {
        path
        type
        sampleValue
        children {
          path
          type
          sampleValue
        }
      }
    }
  }
`;

// Mutation to save field mappings configuration
export const SAVE_SAP_FIELD_MAPPINGS_MUTATION = gql`
  mutation SaveSapFieldMappings($input: SaveSapFieldMappingsInput!) {
    saveSapFieldMappings(input: $input) {
      success
      message
      error
      mappings {
        id
        ourField
        sapFieldPath
        arrayStrategy
        includeInStorage
      }
    }
  }
`;

// Mutation to generate universal search template
export const GENERATE_UNIVERSAL_TEMPLATE_MUTATION = gql`
  mutation GenerateUniversalTemplate($input: GenerateUniversalTemplateInput!) {
    generateUniversalTemplate(input: $input) {
      success
      message
      error
      template {
        id
        name
        description
        sqlText
        category
      }
    }
  }
`;

// Mutation to test SAP query template
export const TEST_SAP_QUERY_TEMPLATE_MUTATION = gql`
  mutation TestSapQueryTemplate($input: TestSapQueryTemplateInput!) {
    testSapQueryTemplate(input: $input) {
      success
      message
      error
      executionTime
      recordCount
      sampleData
      queryMetadata {
        warehouseCode
        searchTerm
        fieldsReturned
      }
    }
  }
`;

// Input types for mutations
export interface SaveSapFieldMappingsInput {
  companyId: string;
  fieldMappings: Array<{
    ourField: string;
    sapFieldPath: string;
    arrayStrategy: string;
    includeInStorage: boolean;
  }>;
  optionalFields: Array<{
    fieldName: string;
    label: string;
    description: string;
    sapFieldPath: string;
    enabled: boolean;
  }>;
}

export interface GenerateUniversalTemplateInput {
  companyId: string;
  templateName: string;
  includeOptionalFields: boolean;
  description?: string;
}

export interface TestSapQueryTemplateInput {
  companyId: string;
  templateId: string;
  warehouseCode: string;
  searchTerm: string;
  limit?: number;
}

// Response types
export interface Company {
  id: string;
  name: string;
  sapBaseUrl: string;
  sapCompanyDb: string;
}

export interface SapFieldMapping {
  id: string;
  ourField: string;
  sapFieldPath: string;
  arrayStrategy: string;
  includeInStorage: boolean;
}

export interface SapQueryTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  sqlText: string;
  updatedAt: string;
}

export interface DiscoveredField {
  path: string;
  type: string;
  sampleValue: string;
  children?: DiscoveredField[];
}

export interface FetchSapSampleDataResponse {
  success: boolean;
  message: string;
  error?: string;
  itemCode?: string;
  discoveredFields: DiscoveredField[];
}

export interface SaveSapFieldMappingsResponse {
  success: boolean;
  message: string;
  error?: string;
  mappings: SapFieldMapping[];
}

export interface GenerateUniversalTemplateResponse {
  success: boolean;
  message: string;
  error?: string;
  template: SapQueryTemplate;
}

export interface TestSapQueryTemplateResponse {
  success: boolean;
  message: string;
  error?: string;
  executionTime: number;
  recordCount: number;
  sampleData: any[];
  queryMetadata: {
    warehouseCode: string;
    searchTerm: string;
    fieldsReturned: string[];
  };
}
